import logger from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { Heart, Building2, BookOpen, Scale, Coins, Star, DollarSign, Loader2, AlertCircle, Copy, CheckCircle, CreditCard, Send } from 'lucide-react';
import { PaymentForm, CreditCard as SquareCreditCard } from 'react-square-web-payments-sdk';
import { donationCategories } from '../data/mock';
import { fetchDonationConfig, submitDonation } from '../services/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const iconMap = {
  Heart: Heart,
  Building2: Building2,
  BookOpen: BookOpen,
  Star: Star,
  HandHeart: Heart,
  Scale: Scale,
  Coins: Coins
};

const DonatePage = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customCategoryText, setCustomCategoryText] = useState('');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    email: '',
    anonymous: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethod] = useState('square');
  const [donationConfig, setDonationConfig] = useState(null);
  const [copied, setCopied] = useState(false);

  const presetAmounts = ['25', '50', '100', '250', '500', '1000'];

  // Helper to get the final category (with custom text for "Other")
  const getFinalCategory = () => {
    if (selectedCategory === 'Other' && customCategoryText.trim()) {
      return `Other: ${customCategoryText.trim()}`;
    }
    return selectedCategory;
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await fetchDonationConfig();
        setDonationConfig(config);
      } catch (err) {
        logger.error('Failed to load donation config:', err);
        // Fallback config
        setDonationConfig({
          etransfer_email: 'donation@fatimia.ca'
        });
      }
    };
    loadConfig();
  }, []);

  const copyEmail = () => {
    if (donationConfig?.etransfer_email) {
      navigator.clipboard.writeText(donationConfig.etransfer_email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Credit card payment is handled by the Square PaymentForm component
  };

  const handleSquarePayment = async (token) => {
    setSubmitting(true);
    setError(null);
    
    const finalAmount = amount === 'custom' ? customAmount : amount;
    const finalCategory = getFinalCategory();

    try {
      const response = await fetch(`${API_BASE_URL}/api/donations/process-payment?source_id=${token.token}&amount=${parseFloat(finalAmount)}&currency=CAD&donor_name=${encodeURIComponent(donorInfo.anonymous ? '' : donorInfo.name)}&donor_email=${encodeURIComponent(donorInfo.email)}&category=${encodeURIComponent(finalCategory)}&is_anonymous=${donorInfo.anonymous}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Payment failed');
      }

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setSelectedCategory('');
        setCustomCategoryText('');
        setAmount('');
        setCustomAmount('');
        setDonorInfo({ name: '', email: '', anonymous: false });
      }, 8000);
    } catch (err) {
      logger.error('Square payment failed:', err);
      setError(err.message || 'Payment processing failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section" style={{ minHeight: '50vh' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2f23] to-[#2a5a3d]" />
        <div className="absolute inset-0 z-2 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '400px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="hero-content container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto animate-fade-in">
            <div className="islamic-divider mb-8">
              <div className="islamic-star" />
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-white mb-6">Support Our Community</h1>
            <p className="text-white/70 text-lg">
              Your generous donations help us maintain our mosque, support Islamic education, 
              and serve those in need. May Allah (SWT) reward you abundantly.
            </p>
            <div className="islamic-divider mt-8">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>

      {/* E-Transfer Highlight Section */}
      <section className="bg-[#f8f6f1] py-12 border-b-2 border-[#c5a572]/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white p-8 border-l-4 border-[#c5a572] shadow-lg">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-[#c5a572]/10 border border-[#c5a572] flex items-center justify-center flex-shrink-0">
                  <Send className="w-8 h-8 text-[#c5a572]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl text-[#1a2f23] mb-2">Preferred: Interac E-Transfer</h3>
                  <p className="text-[#5a6d62] mb-4">
                    The easiest and most direct way to support our community is through Interac E-Transfer.
                  </p>
                  <div className="flex items-center gap-4 bg-[#f8f6f1] p-4 rounded">
                    <span className="text-[#1a2f23] font-medium text-lg">{donationConfig?.etransfer_email || 'donation@fatimia.ca'}</span>
                    <button 
                      onClick={copyEmail}
                      className="flex items-center gap-2 px-4 py-2 bg-[#c5a572] hover:bg-[#d4bb8a] text-white text-sm transition-colors"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy Email
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-sm text-[#5a6d62] mt-3">
                    Please include your name and donation category in the message for your records.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Categories */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Ways to Give</p>
            <h2 className="heading-2 mb-6">Choose a Donation Category</h2>
            <div className="islamic-divider">
              <div className="islamic-star" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            {donationCategories.map((category) => {
              const IconComponent = iconMap[category.icon] || Heart;
              return (
                <div
                  key={category.id}
                  className={`card cursor-pointer transition-all duration-300 text-center ${
                    selectedCategory === category.title
                      ? 'border-[#c5a572] bg-[#c5a572]/5'
                      : 'hover:border-[#c5a572]/50'
                  }`}
                  onClick={() => {
                    setSelectedCategory(category.title);
                    if (category.title !== 'Other') {
                      setCustomCategoryText('');
                    }
                  }}
                >
                  <div className={`w-16 h-16 mx-auto mb-4 border flex items-center justify-center transition-all duration-300 ${
                    selectedCategory === category.title 
                      ? 'border-[#c5a572] bg-[#c5a572] text-white' 
                      : 'border-[#c5a572]/30 text-[#c5a572]'
                  }`}>
                    <IconComponent className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg text-[#1a2f23] mb-2">{category.title}</h3>
                  <p className="text-xs text-[#5a6d62]">{category.description}</p>
                </div>
              );
            })}
          </div>

          {/* Custom Category Input for "Other" */}
          {selectedCategory === 'Other' && (
            <div className="max-w-md mx-auto mb-16">
              <Label htmlFor="custom-category" className="text-sm uppercase tracking-wider text-[#5a6d62]">
                Specify Purpose (Optional)
              </Label>
              <Input
                id="custom-category"
                type="text"
                value={customCategoryText}
                onChange={(e) => setCustomCategoryText(e.target.value.slice(0, 50))}
                placeholder="e.g., In memory of..."
                maxLength={50}
                className="mt-2"
              />
              <p className="text-xs text-[#5a6d62] mt-1 text-right">{customCategoryText.length}/50 characters</p>
            </div>
          )}

          {selectedCategory && selectedCategory !== 'Other' && <div className="mb-8" />}

          {/* Donation Form */}
          <div className="max-w-2xl mx-auto">
            <div className="card card-gold">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 border border-[#c5a572] flex items-center justify-center">
                  <DollarSign className="w-7 h-7 text-[#c5a572]" />
                </div>
                <div>
                  <h3 className="text-2xl text-[#1a2f23]">Make a Donation</h3>
                  <p className="text-sm text-[#5a6d62]">
                    {selectedCategory ? `Donating to: ${getFinalCategory()}` : 'Select a category above to continue'}
                  </p>
                </div>
              </div>
              
              {submitted ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 mx-auto mb-6 border-2 border-[#c5a572] flex items-center justify-center">
                    <Heart className="w-12 h-12 text-[#c5a572]" />
                  </div>
                  <h3 className="text-3xl text-[#1a2f23] mb-3">JazakAllah Khair!</h3>
                  <p className="text-[#5a6d62] text-lg">Thank you for your generous donation.</p>
                  <p className="text-[#5a6d62] mt-2">May Allah (SWT) bless you and your family.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {error}
                    </div>
                  )}

                  {/* Credit Card Payment Info */}
                  <div>
                    <p className="text-sm text-[#5a6d62] p-3 bg-green-50 rounded border border-green-200">
                      <CreditCard className="w-4 h-4 inline mr-2" />
                      Secure credit card payment powered by Square. All major cards accepted.
                    </p>
                  </div>

                  {/* Amount Selection */}
                  <div>
                    <Label className="text-sm uppercase tracking-wider text-[#5a6d62]">Select Amount (CAD)</Label>
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      {presetAmounts.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          className={`py-4 px-4 font-display text-xl transition-all duration-300 ${
                            amount === preset
                              ? 'bg-[#c5a572] text-white'
                              : 'bg-[#f8f6f1] text-[#1a2f23] hover:bg-[#c5a572]/20'
                          }`}
                          onClick={() => setAmount(preset)}
                        >
                          ${preset}
                        </button>
                      ))}
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        className={`w-full py-4 px-4 uppercase tracking-wider transition-all duration-300 ${
                          amount === 'custom'
                            ? 'bg-[#c5a572] text-white'
                            : 'bg-[#f8f6f1] text-[#1a2f23] hover:bg-[#c5a572]/20'
                        }`}
                        onClick={() => setAmount('custom')}
                      >
                        Custom Amount
                      </button>
                      {amount === 'custom' && (
                        <div className="mt-4">
                          <Input
                            type="number"
                            min="1"
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            placeholder="Enter amount"
                            className="text-lg"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Donor Information */}
                  <div className="space-y-5">
                    <div>
                      <Label htmlFor="donor-name" className="text-sm uppercase tracking-wider text-[#5a6d62]">Full Name</Label>
                      <Input
                        id="donor-name"
                        value={donorInfo.name}
                        onChange={(e) => setDonorInfo({ ...donorInfo, name: e.target.value })}
                        placeholder="Your name"
                        className="mt-2"
                        disabled={donorInfo.anonymous}
                      />
                    </div>
                    <div>
                      <Label htmlFor="donor-email" className="text-sm uppercase tracking-wider text-[#5a6d62]">Email (for receipt)</Label>
                      <Input
                        id="donor-email"
                        type="email"
                        value={donorInfo.email}
                        onChange={(e) => setDonorInfo({ ...donorInfo, email: e.target.value })}
                        placeholder="your@email.com"
                        className="mt-2"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="anonymous"
                        checked={donorInfo.anonymous}
                        onChange={(e) => setDonorInfo({ ...donorInfo, anonymous: e.target.checked })}
                        className="w-5 h-5 border-[#c5a572] text-[#c5a572] focus:ring-[#c5a572]"
                      />
                      <Label htmlFor="anonymous" className="cursor-pointer text-[#5a6d62]">
                        Make this donation anonymous
                      </Label>
                    </div>
                  </div>

                  {/* Square Payment Form */}
                  {donationConfig?.square_app_id && donationConfig?.square_location_id && (
                    <div className="mt-6">
                      <PaymentForm
                        applicationId={donationConfig.square_app_id}
                        locationId={donationConfig.square_location_id}
                        cardTokenizeResponseReceived={async (token, buyer) => {
                          if (token.status === 'OK') {
                            await handleSquarePayment(token);
                          } else {
                            setError(token.errors?.[0]?.message || 'Card tokenization failed');
                          }
                        }}
                      >
                        <SquareCreditCard />
                      </PaymentForm>
                      {submitting && (
                        <div className="flex items-center justify-center gap-2 mt-4 text-[#c5a572]">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Processing Payment...</span>
                        </div>
                      )}
                    </div>
                  )}

                  {(!donationConfig?.square_app_id || !donationConfig?.square_location_id) && (
                    <div className="text-center p-4 bg-amber-50 border border-amber-200 rounded">
                      <p className="text-amber-800">Credit card payments are being configured. Please use E-Transfer for now.</p>
                    </div>
                  )}

                  <p className="text-center text-sm text-[#5a6d62]">
                    Tax receipts will be issued for donations over $20.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-dark py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '300px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="islamic-divider mb-8">
              <div className="islamic-star" />
            </div>
            <h2 className="text-4xl text-white mb-8">The Rewards of Giving</h2>
            <div className="quote-box bg-white/5 border-[#c5a572] backdrop-blur-sm">
              <p className="text-white/80 text-lg italic leading-relaxed">
                "The example of those who spend their wealth in the way of Allah is like a seed [of grain] 
                which grows seven spikes; in each spike is a hundred grains. And Allah multiplies [His reward] 
                for whom He wills."
              </p>
              <p className="text-[#c5a572] mt-4 font-display text-lg">— Quran 2:261</p>
            </div>
            <div className="islamic-divider mt-12">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonatePage;
